import { cn } from './cn.js';

export interface SegmentOption<T extends string = string> {
  value: T;
  label: string;
}

export interface SegmentedControlProps<T extends string = string> {
  value: T;
  onChange: (v: T) => void;
  options: SegmentOption<T>[];
  className?: string;
  size?: 'sm' | 'md';
}

export function SegmentedControl<T extends string = string>({
  value,
  onChange,
  options,
  className,
  size = 'md',
}: SegmentedControlProps<T>) {
  return (
    <div
      className={cn(
        'inline-flex items-center gap-1 rounded-2xl border border-line bg-surface-2 p-1 overflow-x-auto scrollbar-none',
        className,
      )}
      role="tablist"
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt.value)}
            className={cn(
              'whitespace-nowrap rounded-xl font-medium tracking-tight transition-all active:scale-95',
              size === 'md' ? 'h-10 px-4 text-sm' : 'h-8 px-3 text-xs',
              active
                ? 'bg-surface-3 text-gold-light border border-gold/25 shadow-glow-sm'
                : 'text-text-dim border border-transparent hover:text-text',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
